/**
 * KitRecordMasthead -- the RECORD-PAGE archetype's masthead (zone 1) with the
 * stage-rail mount (zone 2) directly beneath it.
 *
 * Law homes (cited, never re-derived here):
 *  - RECORD-PAGE archetype, zone 1 MASTHEAD: identity row + status/channel
 *    chips (stage, the ORIGIN attribution chip, a stronger identity chip when
 *    one supersedes it) + a single one-line meta (ref / clinic / captured / a
 *    passive availability fact with its own inline action, e.g. "Phone on file
 *    [Reveal]") + overflow-ONLY actions (the record's WHOLE-RECORD action home
 *    -- overflow dots only, never a visible multi-button row). Zone 2 LIFECYCLE
 *    LINE mounts directly beneath, read-only, no stage writer.
 *    docs/design/design-language/07-BEHAVIOURAL-LAWS.md ("### ARCH", UPDATE 2,
 *    zones 1-2).
 *  - ORIGIN law + SURFACE BINDING rider (07 "### ORIGIN"): on the RECORD-PAGE
 *    archetype the masthead chip binds to the CAPTURE CHANNEL; campaign and
 *    source render in the rail's "Campaign and source" section. This kit only
 *    fixes the archetype default `compactMeta = true` -- the underlying
 *    RecordHeader's compactMeta path calls the SAME `sourceChannelLabel`
 *    channel-derivation the full ladder itself calls, never a second resolver.
 *  - FIVE-SECOND LAW (07 "### 5SEC"): the masthead answers who / where from /
 *    how long waiting / what state without scrolling.
 *  - ANNOTATION-LAYER RIDER (07 "### ARCH" UPDATE 2): mockup annotation boxes
 *    (e.g. "TRIMMED NAV: DESIGN-ONLY - REQUIRES FUNCTIONAL APPROVAL") are
 *    instruction-layer for the builder and are NEVER rendered here as UI.
 *
 * REUSE, NEVER FORK: this composes the ONE shared masthead primitive
 * (record/RecordHeaderBand.tsx `RecordHeader`) with the archetype's fixed
 * defaults (`compactMeta`, `showSummaryStrip=false`) and mounts the stage rail
 * as its immediate sibling. No masthead markup is duplicated here (a second
 * masthead renderer would be a DUPLICATE-WRITER defect). Data arrives via
 * props; this component fetches nothing.
 */
import type { ComponentProps, ReactNode } from 'react';
import { RecordHeader } from '@/dashboard/record/RecordHeaderBand';

export type KitRecordMastheadProps = ComponentProps<typeof RecordHeader> & {
  /**
   * The stage/lifecycle rail mounted directly beneath the masthead (archetype
   * zone 2 -- read-only, no stage writer). OMITTED => no rail renders. The
   * caller owns the rail element so the surface's own stage model stays its
   * own call.
   */
  readonly stageRail?: ReactNode;
};

export default function KitRecordMasthead({
  stageRail,
  compactMeta = true,
  showSummaryStrip = false,
  ...rest
}: KitRecordMastheadProps): JSX.Element {
  return (
    <>
      <RecordHeader
        compactMeta={compactMeta}
        showSummaryStrip={showSummaryStrip}
        {...rest}
      />
      {stageRail}
    </>
  );
}
